import type { Ref } from 'vue';
import {
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';

import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { watchImmediate } from '@vueuse/core';

import type {
  VFormTextEditEmits,
  VFormTextEditProps,
} from '../types';
import type { VariablesPlugin } from './utils';
import {
  getCKEditorDefaultConfig,
} from './utils';

export interface VFormTextEditComposable {
  editorElement: Ref<HTMLDivElement | undefined>;
  wordsCount: Ref<number>;
  charactersCount: Ref<number>;
}

export default function useVFormTextEdit(
  props: RequiredField<VFormTextEditProps, 'variables'>,
  emit: VFormTextEditEmits,
): VFormTextEditComposable {
  const innerValue = ref<string>();

  const editorElement = ref<HTMLDivElement>();
  const wordsCount = ref(0);
  const charactersCount = ref(0);
  const isUnmounted = ref(false);

  function onCharactersUpdate({ words, characters }: { words: number; characters: number }): void {
    wordsCount.value = words;
    charactersCount.value = characters;
  }

  let editor: ClassicEditor | undefined;

  async function destroyEditor(): Promise<void> {
    const editorToDestroy = editor;
    editor = undefined;

    if (!editorToDestroy) {
      return;
    }

    try {
      await editorToDestroy.destroy();
    } catch (error) {
      console.error(error);
    }
  }

  function checkVariables(newValue: string[], oldValue?: string[]): void {
    if (editor && !isUnmounted.value) {
      const plugin = editor.plugins.get('VariablesPlugin') as VariablesPlugin;

      for (const value of (oldValue ?? [])) {
        plugin.removeFromCollection(value);
      }

      for (const value of newValue) {
        plugin.addToCollection(value);
      }

      plugin.setCollectionVisibility(!!newValue.length);
    }
  }

  onMounted(async () => {
    if (editorElement.value) {
      try {
        const createdEditor = await ClassicEditor.create(editorElement.value, getCKEditorDefaultConfig({
          onCharactersUpdate,
        }));

        if (isUnmounted.value) {
          await createdEditor.destroy();
          return;
        }

        editor = createdEditor;

        editor.model.document.on('change:data', () => {
          innerValue.value = editor?.getData();
          emit('input', innerValue.value);
        });

        editor.ui.focusTracker.on('change:isFocused', (evt, name, isFocused) => {
          if (isFocused) {
            emit('focus', innerValue.value);
          } else {
            emit('blur', innerValue.value);
            emit('change', innerValue.value);
          }
        });

        editor.setData(innerValue.value || '');

        checkVariables(props.variables);
      } catch (error) {
        console.error(error);
      }
    }
  });

  onBeforeUnmount(() => {
    isUnmounted.value = true;
    void destroyEditor();
  });

  watchImmediate(() => props.variables, (newValue, oldValue) => {
    checkVariables(newValue, oldValue);
  }, { deep: true });

  watchImmediate(() => props.value, (newValue) => {
    if (innerValue.value !== newValue) {
      innerValue.value = newValue;

      if (editor && !isUnmounted.value) {
        editor.setData(innerValue.value || '');
      }
    }
  });

  return {
    editorElement,
    wordsCount,
    charactersCount,
  };
}
